import { isNumber } from 'lodash';

const competitorDeltaFieldDefinitions = [
  {
    deltaKey: 'competitorDeltaVsPrev',
    deltaSimulatedKey: 'competitorDeltaSimulatedVsPrev',
    liveKey: 'competitorLiveDistance',
    proposedKey: 'competitorProposedDistance',
    unsimulatedProposedKey: 'liveCompetitorProposedDistance',
  },
  {
    deltaKey: 'competitorDeltaVsPrevVolume',
    deltaSimulatedKey: 'competitorDeltaSimulatedVsPrevVolume',
    liveKey: 'competitorLiveDistanceVolume',
    proposedKey: 'competitorProposedDistanceVolume',
    unsimulatedProposedKey: 'liveCompetitorProposedDistanceVolume',
  },
  {
    deltaKey: 'competitorDeltaVsPrevPrice',
    deltaSimulatedKey: 'competitorDeltaSimulatedVsPrevPrice',
    liveKey: 'competitorLiveDistancePrice',
    proposedKey: 'competitorProposedDistancePrice',
    unsimulatedProposedKey: 'liveCompetitorProposedDistancePrice',
  },
];

/**
 * Calculates a mathematical delta when the feature flag is enabled.
 * Consumer must pass the intended proposed value (regular or unsimulated).
 *
 * @param {Object} params - Mathematical delta input parameters.
 * @param {number|null} params.live - The live value supplied by the backend.
 * @param {number|null} params.proposed - The proposed value selected by the consumer.
 * @param {number|null} params.fallback - The backend-computed weighted delta.
 * @param {boolean} params.isEnabled - Whether the mathematical delta feature is active.
 * @returns {number|null} The recomputed delta or the original fallback value.
 */
export function calculateMathematicalDelta({ live, proposed, fallback, isEnabled }) {
  if (!isEnabled) {
    return fallback;
  }

  if (!isNumber(live) || !isNumber(proposed)) {
    return fallback;
  }
  return proposed - live;
}

/**
 * Returns unsimulated proposed value used in mathematical delta calculation.
 *
 * @param {Object} competitor - The competitor entry containing proposed values.
 * @param {string} unsimulatedProposedKey - Unsimulated proposed field key.
 * @returns {number|null} Unsimulated proposed value.
 */
function getUnsimulatedProposedValue(competitor, unsimulatedProposedKey) {
  return competitor[unsimulatedProposedKey];
}

/**
 * Chooses proposed value used for delta calculations.
 *
 * @param {Object} params
 * @param {number|null} params.proposedValue
 * @param {number|null} params.unsimulatedProposedValue
 * @param {boolean} [params.useUnsimulatedProposedDelta=false]
 * @returns {number|null}
 */
export function getProposedValueForDelta({
  proposedValue,
  unsimulatedProposedValue,
  useUnsimulatedProposedDelta = false,
}) {
  if (useUnsimulatedProposedDelta && isNumber(unsimulatedProposedValue)) {
    return unsimulatedProposedValue;
  }
  return proposedValue;
}

/**
 * Applies the mathematical delta calculation to every relevant field for a competitor record.
 *
 * @param {Object} competitor - The competitor entry containing live/proposed/delta values.
 * @param {boolean} isEnabled - Whether the mathematical delta feature is active.
 * @param {boolean} useUnsimulatedProposedDelta - Enables unsimulated-proposed delta behaviour.
 * @returns {Object} A shallow copy of the competitor with updated delta fields.
 */
export function applyMathematicalDeltaToCompetitor(
  competitor,
  isEnabled,
  useUnsimulatedProposedDelta = false
) {
  if (!isEnabled || !competitor) {
    return competitor;
  }
  const updatedCompetitor = { ...competitor };
  competitorDeltaFieldDefinitions.forEach(
    ({ deltaKey, deltaSimulatedKey, liveKey, proposedKey, unsimulatedProposedKey }) => {
      const proposedValue = updatedCompetitor[proposedKey];
      const unsimulatedProposedValue = getUnsimulatedProposedValue(
        updatedCompetitor,
        unsimulatedProposedKey
      );
      const proposedValueForDelta = getProposedValueForDelta({
        proposedValue,
        unsimulatedProposedValue,
        useUnsimulatedProposedDelta,
      });
      updatedCompetitor[deltaKey] = calculateMathematicalDelta({
        live: updatedCompetitor[liveKey],
        proposed: proposedValueForDelta,
        fallback: updatedCompetitor[deltaKey],
        isEnabled,
      });
      if (useUnsimulatedProposedDelta) {
        // when using unsimulated proposed value for delta, also calculate the simulated delta for comparison
        updatedCompetitor[deltaSimulatedKey] = calculateMathematicalDelta({
          live: updatedCompetitor[liveKey],
          proposed: proposedValue,
          isEnabled,
        });
      }
    }
  );
  return updatedCompetitor;
}

/**
 * Transforms every competitor entry in a keyed map with the mathematical delta logic.
 *
 * @param {Object<string, Object>} competitorsByKey - Map of competitor entries keyed by competitorKey.
 * @param {boolean} isEnabled - Whether the mathematical delta feature is active.
 * @param {boolean} useUnsimulatedProposedDelta - Enables unsimulated-proposed delta behaviour.
 * @returns {Object<string, Object>} A new map with recalculated delta values.
 */
export function transformCompetitorsWithMathDelta(
  competitorsByKey,
  isEnabled,
  useUnsimulatedProposedDelta = false
) {
  if (!isEnabled || !competitorsByKey) {
    return competitorsByKey;
  }
  return Object.fromEntries(
    Object.entries(competitorsByKey).map(([key, competitor]) => [
      key,
      applyMathematicalDeltaToCompetitor(competitor, isEnabled, useUnsimulatedProposedDelta),
    ])
  );
}
